<template>
  <div class="sip-trunk-wrapper">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Line management
        </h1>
      </div>

      <div class="main-content">
        <div v-if="loadingRequest" class="loading-state">
          <v-progress-circular indeterminate color="primary" size="48"/>
          <p class="loading-text">
            Loading…
          </p>
        </div>

        <v-card v-else-if="existingRequest" class="status-card" outlined>
          <div class="status-card-inner">
            <div class="status-icon-wrap">
              <svg class="status-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div class="status-body">
              <h3 class="status-title">
                Request received
              </h3>
              <p v-if="existingRequest.info_message" class="status-message">
                {{ existingRequest.info_message }}
              </p>
              <div class="status-meta">
                <span class="status-badge">Status: <strong>Under review</strong></span>
                <span v-if="existingRequest.created_at" class="status-date">
                  Date: {{ formatDate(existingRequest.created_at) }}
                </span>
              </div>
            </div>
          </div>
        </v-card>

        <v-card v-else class="wizard-card" outlined>
          <div class="card-inner">
            <h2 class="card-title">
              Line integration wizard
            </h2>
            <p class="card-subtitle">
              Tell us your situation so we can get you set up.
            </p>

            <div class="question-block">
              <p class="question-label">
                1. Do you already have a phone line?
              </p>
              <div class="options-row">
                <div
                  class="option-panel"
                  :class="{ selected: form.hasLine === true }"
                  @click="selectHasLine(true)">
                  <span class="option-title">Yes, I have one</span>
                  <span class="option-desc">I want to use my existing number</span>
                </div>
                <div
                  class="option-panel"
                  :class="{ selected: form.hasLine === false }"
                  @click="selectHasLine(false)">
                  <span class="option-title">No, I don’t</span>
                  <span class="option-desc">Assign a new number</span>
                </div>
              </div>
            </div>

            <div v-if="showSecondSection" class="question-block">
              <p class="question-label">
                2. Do you have SIP (VoIP) details?
              </p>
              <p class="question-hint">
                IP, port, username and password from your PBX or provider.
              </p>
              <div class="options-row">
                <div
                  class="option-panel"
                  :class="{ selected: form.hasSipInfo === true }"
                  @click="form.hasSipInfo = true">
                  <span class="option-title">Yes, I have them</span>
                </div>
                <div
                  class="option-panel"
                  :class="{ selected: form.hasSipInfo === false }"
                  @click="form.hasSipInfo = false">
                  <span class="option-title">No / Not sure</span>
                </div>
              </div>
            </div>

            <v-btn
              color="primary"
              class="submit-btn"
              elevation="0"
              :loading="loading"
              :disabled="!canSubmit || loading"
              @click="createRequest">
              Submit request
            </v-btn>

            <p class="footnote">
              * After you submit, our team will contact you.
            </p>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/plugins/axios';

export default {
  name: 'SipTrunk',
  data() {
    return {
      loading: false,
      loadingRequest: true,
      existingRequest: null,
      showSecondSection: false,
      form: {
        hasLine: null,
        hasSipInfo: null,
      },
    };
  },
  async mounted() {
    await this.fetchLineRequest();
  },
  computed: {
    canSubmit() {
      if (this.form.hasLine === false) return true;
      if (this.form.hasLine === true && this.form.hasSipInfo !== null) return true;
      return false;
    },
  },
  methods: {
    async fetchLineRequest() {
      this.loadingRequest = true;
      this.existingRequest = null;
      try {
        const { data } = await apiClient.get('/line-request');
        const list = data?.data;
        if (Array.isArray(list) && list.length > 0) {
          this.existingRequest = list[0];
        }
      } catch (err) {
        if (err.response?.status !== 401) {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            message: err.response?.data?.message || 'Could not load request details.',
            timeout: 5000,
          });
        }
      } finally {
        this.loadingRequest = false;
      }
    },
    formatDate(isoString) {
      if (!isoString) return '';
      try {
        return new Date(isoString).toLocaleDateString('en-US');
      } catch {
        return isoString;
      }
    },
    selectHasLine(hasLine) {
      this.form.hasLine = hasLine;
      this.showSecondSection = hasLine === true;
      if (!hasLine) this.form.hasSipInfo = null;
    },
    async createRequest() {
      const payload = {
        has_line: this.form.hasLine,
        has_sip_info: this.form.hasLine === true ? Boolean(this.form.hasSipInfo) : false,
      };

      this.loading = true;
      try {
        await apiClient.post('/line-request', payload);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message: 'Your request was submitted. Our team will contact you shortly.',
          timeout: 5000,
        });
        this.form.hasSipInfo = null;
        await this.fetchLineRequest();
      } catch (err) {
        if (!err.response || err.response.status !== 401) {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            message: err.response?.data?.message || 'Something went wrong while submitting your request.',
            timeout: 5000,
          });
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.main-content {
  padding: 24px;
  align-items: center;
  overflow: auto;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px;
  width: 100%;
  max-width: 720px;
}

.loading-text {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: $body-3-size;
  color: $light-foreground-4;
  margin: 0;
}

.status-card {
  width: 100%;
  max-width: 672px; /* max-w-3xl = 48rem */
  border-radius: 12px;
  border-left: 4px solid $light-info;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.status-card-inner {
  padding: 24px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.status-icon-wrap {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba($light-info, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-icon {
  width: 24px;
  height: 24px;
  color: $light-info;
}

.status-body {
  flex: 1;
  min-width: 0;
}

.status-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: $text-size-18;
  line-height: 1.4em;
  color: $light-foreground-1;
  margin: 0 0 8px 0;
}

.status-message {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: $body-2-size;
  line-height: 1.5em;
  color: $light-foreground-3;
  margin: 0 0 16px 0;
}

.status-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: $body-3-size;
  color: $light-foreground-4;
}

.status-badge strong {
  color: $light-info;
  font-weight: 600;
}

.status-date {
  color: $light-foreground-4;
}

.wizard-card {
  width: 100%;
  max-width: 720px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid $light-border;
}

.card-inner {
  padding: 32px;
}

.card-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: $text-size-18;
  line-height: 1.4em;
  color: $light-foreground-1;
  margin: 0 0 8px 0;
}

.card-subtitle {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: $body-3-size;
  line-height: 1.4em;
  color: $light-foreground-3;
  margin: 0 0 28px 0;
}

.question-block {
  margin-bottom: 28px;
}

.question-label {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: $text-size-13;
  line-height: 1.4em;
  color: $light-foreground-1;
  margin: 0 0 12px 0;
  letter-spacing: 0.02em;
}

.question-hint {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: $text-size-13;
  line-height: 1.4em;
  color: $light-foreground-4;
  margin: -4px 0 12px 0;
}

.options-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.option-panel {
  flex: 1;
  min-width: 200px;
  padding: 16px 20px;
  border-radius: 8px;
  border: 2px solid $light-border;
  background: $light-surface-1;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;

  &.selected {
    border-color: $light-info;
    background: rgba($light-info, 0.04);
  }

  &:hover:not(.selected) {
    border-color: $light-gray-light;
  }
}

.option-title {
  display: block;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: $body-3-size;
  line-height: 1.4em;
  color: $light-foreground-1;
}

.option-desc {
  display: block;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: $text-size-13;
  line-height: 1.4em;
  color: $light-foreground-3;
  margin-top: 4px;
}

.submit-btn {
  margin-top: 8px;
  margin-bottom: 16px;
  text-transform: none;
  font-weight: 600;
  padding: 10px 24px;
}

.footnote {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: $caption-1-size;
  font-style: italic;
  color: $light-foreground-4;
  margin: 0;
}
</style>
